import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaFileExcel, FaArrowLeft, FaDownload, FaChartBar } from "react-icons/fa";
import { useProyectoCliente } from '../../services/hooks/useProyectoCliente';
import { useComedores } from '../../services/hooks/useComedores';
import { reporteApi } from '../../services/api/reporteApi';
import { downloadExcelReport } from '../../services/utils/downloadHelper';
import "react-toastify/dist/ReactToastify.css";

const ReporteComensalesComedor = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));
  
  // Usar los hooks para las operaciones de API
  const { comedores, fetchComedoresByProyecto, loading: loadingComedores } = useComedores();
  const { 
    proyectosAsignados: clientesDelProyecto, 
    fetchClientesByProyecto,
    loading: loadingClientes 
  } = useProyectoCliente();

  const [formData, setFormData] = useState({
    id_cliente: "",
    id_proyecto: selectedProject ? selectedProject.id : "",
    id_comedor: "",
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!token || !currentUser) {
      navigate("/");
    }
  }, [navigate, token, currentUser]);

  // Cargar datos iniciales
  useEffect(() => {
    if (selectedProject?.id && currentUser?.id) {
      fetchData();
    }
  }, [selectedProject?.id, currentUser?.id]);

  const fetchData = useCallback(async () => {
    try {
      await Promise.all([
        fetchComedoresByProyecto(selectedProject.id),
        fetchClientesByProyecto(selectedProject.id)
      ]);
    } catch (error) {
      console.error("Error al cargar los datos:", error);
      toast.error("Error al cargar los datos iniciales");
    }
  }, [selectedProject?.id, fetchComedoresByProyecto, fetchClientesByProyecto]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value.trim() === "" ? "" : value,
    });
  };

  const handleGenerarReporte = async (e) => {
    e.preventDefault();

    if (!formData.id_cliente || !formData.id_comedor) {
      toast.error("Por favor seleccione cliente y comedor");
      return;
    }

    const params = {
      id_proyecto: formData.id_proyecto,
      id_cliente: formData.id_cliente,
      id_comedor: formData.id_comedor
    };

    try {
      // Descarga directa de Excel sin visualización previa
      await downloadExcelReport(
        reporteApi.descargarReporteComensalesComedorExcel,
        params,
        `Reporte_Comensales_Comedor_${new Date().toISOString().split('T')[0]}.xlsx`
      );
      toast.success('Reporte descargado exitosamente');
    } catch (error) {
      console.error("Error al generar reporte:", error);
      toast.error(error.message || 'Error al descargar el reporte');
    }
  };

  return (
    <>
      {/* Digital Harmony Styles para Reportes */}
      <style>
        {`
          /* Fondo degradado */
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.5rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            font-weight: 700;
          }
          
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          .dh-card-header-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
          }
          
          .dh-card-header-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            color: white;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(0, 198, 255, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-info:hover {
            background: linear-gradient(135deg, #0072ff 0%, #0056d1 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 198, 255, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Campos de formulario modernos */
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          .dh-form-label {
            color: #374151;
            font-weight: 600;
            font-size: 0.95rem;
            margin-bottom: 8px;
          }
          
          /* Tabla mejorada */
          .dh-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          }
          
          .dh-table thead th {
            background: #374151;
            color: white;
            font-weight: 700;
            font-size: 0.875rem;
            text-transform: uppercase;
            padding: 1rem 0.75rem;
            border: none;
          }
          
          .dh-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
          }
          
          .dh-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%);
          }
          
          /* Badges modernos */
          .dh-badge-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
          }
          
          .dh-badge-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
          }
          
          /* Alertas modernas */
          .dh-alert {
            border: none;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }
          
          .dh-alert-info {
            background: linear-gradient(135deg, rgba(0, 198, 255, 0.1) 0%, rgba(0, 114, 255, 0.1) 100%);
            border-left: 4px solid #00c6ff;
            color: #0056d1;
          }
        `}
      </style>

      <div className="dh-reporte-bg">
        <ToastContainer />
        
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaChartBar className="me-3" />
                  Reporte de Comensales por Comedor
                </h1>
                <p className="dh-main-subtitle">Generación y exportación de reportes detallados</p>
              </div>
              <Button 
                className="btn-dh-secondary d-flex align-items-center"
                onClick={() => navigate("/home")}
              >
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            <Col lg={8}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-primary">
                  <h5 className="mb-0">
                    <FaFileExcel className="me-2" />
                    Configuración del Reporte
                  </h5>
                </Card.Header>
                <Card.Body style={{ padding: '2rem' }}>
                  <Form onSubmit={handleGenerarReporte}>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Proyecto</Form.Label>
                          <Form.Control
                            type="text"
                            value={selectedProject?.nombre || ''}
                            disabled
                            className="dh-form-control"
                            style={{ background: '#f8f9fa', color: '#6b7280' }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Cliente *</Form.Label>
                          <Form.Select
                            name="id_cliente"
                            value={formData.id_cliente}
                            onChange={handleInputChange}
                            required
                            disabled={loadingClientes}
                            className="dh-form-control"
                          >
                            <option value="">
                              {loadingClientes ? 'Cargando clientes...' : 'Seleccione un cliente'}
                            </option>
                            <option value="Todos">Todos los clientes</option>
                            {clientesDelProyecto?.map((cliente) => (
                              <option key={cliente.id} value={cliente.id}>
                                {cliente.nombre_cliente || cliente.nombre}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Comedor *</Form.Label>
                          <Form.Select
                            name="id_comedor"
                            value={formData.id_comedor}
                            onChange={handleInputChange}
                            required
                            disabled={loadingComedores}
                            className="dh-form-control"
                          >
                            <option value="">
                              {loadingComedores ? 'Cargando comedores...' : 'Seleccione un comedor'}
                            </option>
                            {comedores?.map((comedor) => (
                              <option key={comedor.id} value={comedor.id}>
                                {comedor.nombre}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col className="d-flex gap-2 align-items-center">
                        <Button 
                          type="submit" 
                          disabled={!formData.id_cliente || !formData.id_comedor}
                          className="btn-dh-primary d-flex align-items-center"
                        >
                          <FaDownload className="me-2" />
                          Generar Reporte Excel
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>

            </Col>

            <Col lg={4}>

              {/* Instrucciones */}
              <Card className="dh-card mt-4">
                <Card.Header className="dh-card-header dh-card-header-primary">
                  <h6 className="mb-0">
                    <FaFileExcel className="me-2" />
                    Instrucciones
                  </h6>
                </Card.Header>
                <Card.Body style={{ padding: '1.5rem' }}>
                  <div className="dh-alert dh-alert-info">
                    <strong>Descarga Directa:</strong>
                    <p className="mb-2 mt-2">
                      Al hacer clic en "Generar Reporte Excel" se descargará automáticamente 
                      un archivo Excel con todos los datos de comensales del comedor seleccionado.
                    </p>
                    <small className="text-muted">
                      El archivo incluye información completa: nombres, documentos, cargos, empresas y más.
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ReporteComensalesComedor;
